import { unwrapResult } from "@reduxjs/toolkit";
import AddLinkIcon from "@mui/icons-material/AddLink";
import AddTaskIcon from "@mui/icons-material/AddTask";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { Localized } from "@fluent/react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import React, { useEffect, useMemo, useState } from "react";
import { APIError, AppId } from "../../../../app/appTypes";
import { CancelButton } from "../../../../app/components/CancelButton";
import { LpField } from "../../../../app/components/LpField";
import { YesButton } from "../../../../app/components/YesButton";
import { useAppDispatch } from "../../../../app/hooks";
import ValidationProcessWizard from "../../validationProcess/ValidationProcessWizard";
import { ValidationProcessStatusPill } from "../../validationProcess/components/ValidationProcessStatusPill";
import { LinkValidationProjectProcessDialog, LinkValidationProjectProcessDialogProps } from "../dialogs/LinkValidationProjectProcessDialog";
import { loadValidationProjectProcesses, unlinkValidationProjectProcess, ValidationProjectProcessDto } from "../validationProjectSlice";
import { useEntityNavigation } from "../../../../app/components/EntityBreadcrumbs";

type DialogState =
    { tag: "none" }
    | { tag: "linkProcess", props: LinkValidationProjectProcessDialogProps }
    | { tag: "confirmUnlinkProcess", process: ValidationProjectProcessDto };

interface ProjectProcessesTabProps {
    projectId: AppId;
}

export const ProjectProcessesTab = ({ projectId }: ProjectProcessesTabProps) => {
    const dispatch = useAppDispatch();
    const navigateToEntity = useEntityNavigation({ type: "project", id: projectId });
    const [processes, setProcesses] = useState<ValidationProjectProcessDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<APIError | null>(null);
    const [isWizardOpen, setIsWizardOpen] = useState(false);
    const [unlinkingProcessId, setUnlinkingProcessId] = useState<AppId | null>(null);
    const [dialog, setDialog] = useState<DialogState>({ tag: "none" });

    const reloadProcesses = React.useCallback(() => {
        setLoading(true);
        return dispatch(loadValidationProjectProcesses(projectId))
            .then(unwrapResult)
            .then((result) => {
                setProcesses(result);
                setError(null);
            })
            .catch((err) => setError(err))
            .finally(() => setLoading(false));
    }, [dispatch, projectId]);

    useEffect(() => {
        reloadProcesses();
    }, [reloadProcesses]);

    const handleOpenValidationProcessClick = (processId: AppId) => {
        navigateToEntity({ type: "process", id: processId });
    };

    const handleConfirmUnlinkProcessClick = (process: ValidationProjectProcessDto) => {
        setUnlinkingProcessId(String(process.id));
        dispatch(unlinkValidationProjectProcess({ projectId, processId: String(process.id) }))
            .then(unwrapResult)
            .then(() => {
                setProcesses((current) => current.filter((item) => item.id !== process.id));
                setError(null);
                setDialog({ tag: "none" });
            })
            .catch((err) => setError(err))
            .finally(() => setUnlinkingProcessId(null));
    };

    const columns = useMemo<GridColDef[]>(() => [
        {
            field: "lp",
            headerName: "Lp",
            width: 80,
            sortable: false,
            filterable: false,
            align: "center",
            headerAlign: "center",
            disableColumnMenu: true,
            renderCell: (params) => (
                <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <LpField value={params.api.getRowIndexRelativeToVisibleRows(params.row.id) + 1} />
                </Box>
            ),
        },
        { field: "no", headerName: "No", width: 180 },
        { field: "name", headerName: "Name", flex: 1, minWidth: 260 },
        {
            field: "status",
            headerName: "Status",
            width: 180,
            minWidth: 180,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => <ValidationProcessStatusPill status={params.value} size="small" dense />,
        },
        {
            field: "startDate",
            headerName: "Start",
            align: "center",
            headerAlign: "center",
            width: 130,
            minWidth: 120,
            maxWidth: 150,
            valueGetter: (_, row) => row.startDate ? new Date(row.startDate).toISOString().split("T")[0] : "-",
        },
        {
            field: "finishDate",
            headerName: "Finish",
            align: "center",
            headerAlign: "center",
            width: 130,
            minWidth: 120,
            maxWidth: 150,
            valueGetter: (_, row) => row.finishDate ? new Date(row.finishDate).toISOString().split("T")[0] : "-",
        },
        {
            field: "actions",
            headerName: "Akcje",
            width: 130,
            sortable: false,
            filterable: false,
            align: "center",
            headerAlign: "center",
            disableColumnMenu: true,
            renderCell: (params) => (
                <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                        <Tooltip title="Przejdź do procesu walidacyjnego">
                            <IconButton
                                size="small"
                                sx={{
                                    color: "primary.main",
                                    "&:hover": {
                                        color: "success.main",
                                        bgcolor: "success.lighter",
                                    },
                                }}
                                onClick={() => handleOpenValidationProcessClick(String(params.row.id))}>
                                <FactCheckIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Odepnij proces od projektu">
                            <span>
                                <IconButton
                                    size="small"
                                    sx={{
                                        color: "error.main",
                                        "&:hover": {
                                            color: "error.dark",
                                            bgcolor: "error.lighter",
                                        },
                                    }}
                                    disabled={unlinkingProcessId === String(params.row.id)}
                                    onClick={() => setDialog({ tag: "confirmUnlinkProcess", process: params.row as ValidationProjectProcessDto })}>
                                    <LinkOffIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </Box>
            ),
        },
    ], [navigateToEntity, projectId, unlinkingProcessId]);

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            {error && <Alert severity="error">{error.kind === "http" ? error.problem?.title ?? "Request failed" : "Request failed"}</Alert>}
            <DataGrid
                autoHeight
                loading={loading}
                rows={processes}
                columns={columns}
            />
            <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<AddTaskIcon />}
                sx={{ mt: 2 }}
                onClick={() => setIsWizardOpen(true)}>
                Dodaj proces walidacyjny
            </Button>
            <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<AddLinkIcon />}
                sx={{ mt: 2, ml: 1 }}
                onClick={() => setDialog({
                    tag: "linkProcess",
                    props: {
                        projectId,
                        onSave: (process) => {
                            setProcesses((current) => {
                                const index = current.findIndex((item) => item.id === process.id);
                                if (index >= 0) {
                                    const next = [...current];
                                    next[index] = process;
                                    return next;
                                }
                                return [...current, process];
                            });
                            setDialog({ tag: "none" });
                        },
                        onCancel: () => setDialog({ tag: "none" }),
                    },
                })}>
                Podlinkuj proces
            </Button>
            {isWizardOpen && (
                <ValidationProcessWizard
                    projectId={projectId}
                    onClose={() => setIsWizardOpen(false)}
                    onSave={(process) => {
                        reloadProcesses();
                        setIsWizardOpen(false);
                        navigateToEntity({ type: "process", id: String(process.id) });
                    }}
                />
            )}
            {dialog.tag === "linkProcess" && <LinkValidationProjectProcessDialog {...dialog.props} />}
            {dialog.tag === "confirmUnlinkProcess" && (
                <Dialog open fullWidth maxWidth="sm">
                    <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <WarningAmberIcon color="warning" />
                        <Typography component="span" variant="h6">
                            <Localized id="validation-project-unlink-process-title">
                                <span>Potwierdź odlinkowanie</span>
                            </Localized>
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            <Localized id="validation-project-unlink-process-confirm">
                                <span>Czy na pewno chcesz odlinkować ten proces od projektu?</span>
                            </Localized>
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <YesButton
                            loading={unlinkingProcessId === String(dialog.process.id)}
                            onClick={() => handleConfirmUnlinkProcessClick(dialog.process)}
                        />
                        <CancelButton
                            loading={unlinkingProcessId === String(dialog.process.id)}
                            onClick={() => setDialog({ tag: "none" })}
                        />
                    </DialogActions>
                </Dialog>
            )}
        </Paper>
    );
};